'use client'

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Grid,
  HStack,
  Input,
  RadioGroup,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { AppCard } from '@/shared'
import {
  useGetSurveyHistoryDetailQuery,
  useGetSurveyHistoryQuery,
} from '@/entities/questionnaire/api'
import type {
  ISurveyHistoryAnswer,
  ISurveyHistoryDecision,
} from '@/entities/questionnaire/types'
import type { LocaleType } from '@/shared/types'

interface Props {
  donorId: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('ru-RU')
}

function getCategoryLabel(answer: ISurveyHistoryAnswer, language: string) {
  switch (language as LocaleType) {
    case 'uz':
      return answer.categoryNameUzLat
    case 'uz-Cyrl':
      return answer.categoryNameUzCyr
    default:
      return answer.categoryNameRu
  }
}

// ─── Read-only answer field ────────────────────────────────────────────────────

function AnswerField({ answer }: { answer: ISurveyHistoryAnswer }) {
  const { t } = useTranslation()

  return (
    <Box
      py={3}
      borderBottomWidth="1px"
      borderColor="border.muted"
      _last={{ borderBottomWidth: 0 }}
    >
      <Text fontSize="sm" mb={2} lineHeight={1.5}>
        {answer.questionText}
      </Text>

      {answer.questionType === 'BOOLEAN' && (
        <RadioGroup.Root
          value={
            answer.answerBoolean === null ? '' : String(answer.answerBoolean)
          }
          disabled
        >
          <HStack gap={6}>
            <RadioGroup.Item value="true">
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText fontSize="sm">
                {t('common.yes')}
              </RadioGroup.ItemText>
            </RadioGroup.Item>
            <RadioGroup.Item value="false">
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText fontSize="sm">
                {t('common.no')}
              </RadioGroup.ItemText>
            </RadioGroup.Item>
          </HStack>
        </RadioGroup.Root>
      )}

      {answer.questionType === 'DATE' && (
        <Input
          type="date"
          size="sm"
          value={answer.answerDetails ?? ''}
          disabled
          bg="bg.surface"
          borderColor="border"
          borderRadius="md"
          maxW="240px"
          readOnly
        />
      )}

      {answer.questionType === 'TEXT' && (
        <Textarea
          size="sm"
          value={answer.answerDetails ?? ''}
          disabled
          bg="bg.surface"
          borderColor="border"
          borderRadius="md"
          resize="vertical"
          rows={3}
          readOnly
        />
      )}
    </Box>
  )
}

// ─── Decision banner ────────────────────────────────────────────────────────────

function DecisionBanner({ decision }: { decision: ISurveyHistoryDecision }) {
  const { t } = useTranslation()

  if (!decision.deferred) {
    return (
      <Box
        bg="badge.green.20"
        borderWidth="1px"
        borderColor="badge.green.100"
        borderRadius="xl"
        px={4}
        py={3}
      >
        <Text fontSize="sm" fontWeight="medium" color="badge.green.100">
          {t('donor_detail.medical_survey.history.not_deferred')}
        </Text>
      </Box>
    )
  }

  return (
    <Box
      bg="badge.red.20"
      borderWidth="1px"
      borderColor="badge.red.100"
      borderRadius="xl"
      px={4}
      py={3}
    >
      <VStack align="stretch" gap={1}>
        <Text fontSize="sm" fontWeight="medium" color="badge.red.100">
          {decision.permanent
            ? t('donor_detail.medical_survey.history.deferred_permanent')
            : t('donor_detail.medical_survey.history.deferred')}
        </Text>
        {!decision.permanent && decision.deferralUntil && (
          <Text fontSize="xs" color="fg.muted">
            {t('donor_detail.medical_survey.history.deferral_until', {
              date: formatDate(decision.deferralUntil),
            })}
          </Text>
        )}
        {decision.reasonCodes.length > 0 && (
          <Text fontSize="xs" color="fg.muted">
            {decision.reasonCodes.join(', ')}
          </Text>
        )}
      </VStack>
    </Box>
  )
}

// ─── Detail view ────────────────────────────────────────────────────────────────

function SurveyHistoryDetail({
  historyId,
  onBack,
}: {
  historyId: string
  onBack: () => void
}) {
  const { t, i18n } = useTranslation()
  const { data, isPending } = useGetSurveyHistoryDetailQuery(historyId)

  const detail = data?.data

  const groupedCategories = useMemo(() => {
    const map = new Map<string, ISurveyHistoryAnswer[]>()

    ;(detail?.content.answers ?? []).forEach((answer) => {
      const existing = map.get(answer.categoryKey)
      map.set(answer.categoryKey, [...(existing ?? []), answer])
    })

    return [...map.entries()]
      .map(([categoryKey, answers]) => ({
        categoryKey,
        label: getCategoryLabel(answers[0], i18n.language),
        answers: [...answers].sort((a, b) => a.sortOrder - b.sortOrder),
      }))
      .sort(
        (a, b) =>
          (a.answers[0]?.sortOrder ?? 0) - (b.answers[0]?.sortOrder ?? 0),
      )
  }, [detail, i18n.language])

  return (
    <VStack gap={5} align="stretch">
      <HStack justify="space-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          borderRadius="md"
          color={{ _dark: 'white', _light: 'fg' }}
          onClick={onBack}
        >
          {t('donor_detail.medical_survey.history.back')}
        </Button>
        {detail && (
          <Text fontSize="sm" color="fg.muted">
            {formatDate(detail.performed_at)} · {detail.doctor_fio}
          </Text>
        )}
      </HStack>

      {isPending ? (
        <Box display="flex" justifyContent="center" py={10}>
          <Spinner size="lg" color="primary.fg" />
        </Box>
      ) : (
        <>
          {groupedCategories.map(({ categoryKey, label, answers }) => (
            <AppCard.Root key={categoryKey}>
              <AppCard.Body p={5}>
                <Text fontWeight="semibold" mb={3}>
                  {label}
                </Text>
                <VStack gap={0} align="stretch" divideY="1px">
                  {answers.map((answer) => (
                    <AnswerField key={answer.question_id} answer={answer} />
                  ))}
                </VStack>
              </AppCard.Body>
            </AppCard.Root>
          ))}

          {detail?.content.decision && (
            <DecisionBanner decision={detail.content.decision} />
          )}
        </>
      )}
    </VStack>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SurveyHistoryList({ donorId }: Props) {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isPending } = useGetSurveyHistoryQuery(donorId, {
    enabled: !!donorId,
  })

  if (selectedId) {
    return (
      <SurveyHistoryDetail
        historyId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    )
  }

  const history = data?.data ?? []

  const columns = [
    t('donor_detail.medical_survey.history.col_date'),
    t('donor_detail.medical_survey.history.col_doctor'),
    t('donor_detail.medical_survey.history.col_decision'),
  ]

  return (
    <AppCard.Root>
      <AppCard.Body p={5}>
        <Text fontWeight="semibold" fontSize="sm" mb={4}>
          {t('donor_detail.medical_survey.history.title')}
        </Text>

        {isPending ? (
          <Box display="flex" justifyContent="center" py={10}>
            <Spinner size="lg" color="primary.fg" />
          </Box>
        ) : history.length === 0 ? (
          <Text fontSize="sm" color="fg.muted" py={4}>
            {t('donor_detail.medical_survey.history.empty')}
          </Text>
        ) : (
          <>
            <Grid
              templateColumns="120px 1fr 1fr"
              gap={2}
              pb={2}
              borderBottomWidth="1px"
              borderColor="border"
            >
              {columns.map((col) => (
                <Text
                  key={col}
                  fontSize="xs"
                  color="fg.muted"
                  fontWeight="semibold"
                >
                  {col}
                </Text>
              ))}
            </Grid>

            <VStack gap={0} align="stretch">
              {history.map((row) => (
                <Grid
                  key={row.id}
                  templateColumns="120px 1fr 1fr"
                  gap={2}
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="border.muted"
                  _last={{ borderBottom: 'none' }}
                  alignItems="center"
                  cursor="pointer"
                  _hover={{ bg: 'bg.subtle' }}
                  onClick={() => setSelectedId(row.id)}
                >
                  <Text fontSize="sm" color="fg.muted">
                    {formatDate(row.performedAt)}
                  </Text>
                  <Text fontSize="sm">{row.doctorFio}</Text>
                  <Text fontSize="sm" color="fg.muted" truncate>
                    {row.decisionLabel}
                  </Text>
                </Grid>
              ))}
            </VStack>
          </>
        )}
      </AppCard.Body>
    </AppCard.Root>
  )
}
